/**
 * TruBeauté — explicit funnel events with a provider switch.
 * PostHog mode uses the shared anonymous batch transport; notes below describe legacy mode.
 *
 * Differs from `lib/analytics.js` in three critical ways:
 *  1. Stealth Mode is a hard analytics kill-switch. No event payload or
 *     analytics session identifier leaves the browser while it is on.
 *  2. We strip privacy-sensitive context BEFORE the POST: only the
 *     referrer domain is sent (never full URL), device_type is
 *     coarse-bucketed (mobile/tablet/desktop), locale comes from
 *     the i18n setting, route is the pathname (no querystring).
 *  3. Posts to /api/analytics/event (new endpoint) instead of
 *     /api/events. The new endpoint also never reads IP/UA — full
 *     defense-in-depth.
 *
 * Usage:
 *   import { trackFP } from "../lib/firstPartyAnalytics";
 *   trackFP("sample_routine_view");
 *   trackFP("checkout_started", { plan: "annual" });
 *
 * The session id is shared with lib/analytics.js (ANALYTICS_SESSION_KEY)
 * so the same browser tab is recognizable across both pipelines. It's
 * still a TruBeauté-generated UUID v4 in sessionStorage — dies with
 * the tab, never linked to a user account.
 */
import axios from "axios";
import i18n from "../i18n";
import { safeLocal, safeSession } from "./safeStorage";
import { STEALTH_KEY, ANALYTICS_SESSION_KEY } from "./storageKeys";
import { usesPostHog, capturePostHog } from "./posthogAnalytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Same key as legacy analytics — one anonymous session id per tab.
const _uuid = () =>
  ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
    (
      c ^
      (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))
    ).toString(16),
  );

const _getSessionId = () => {
  let id = safeSession.getItem(ANALYTICS_SESSION_KEY);
  if (!id) {
    id = _uuid();
    safeSession.setItem(ANALYTICS_SESSION_KEY, id);
  }
  return id;
};

/** True if the user has Stealth Mode enabled in this browser. */
const _isStealth = () => safeLocal.getItem(STEALTH_KEY) === "1";

const _isEphemeralDemoRoute = () =>
  typeof window !== "undefined" &&
  (window.location.pathname === "/demo" ||
    window.location.pathname.startsWith("/demo/"));

/**
 * Coarse device bucket from viewport width + touch capability.
 * No userAgent parsing — UA strings are PII-adjacent (fingerprintable).
 * Returns "mobile" | "tablet" | "desktop" | "unknown".
 */
const _detectDeviceType = () => {
  if (typeof window === "undefined" || !window.matchMedia) return "unknown";
  try {
    const w = window.innerWidth || 0;
    const isTouch =
      "ontouchstart" in window || (navigator.maxTouchPoints || 0) > 0;
    if (w < 768 && isTouch) return "mobile";
    if (w >= 768 && w < 1024 && isTouch) return "tablet";
    return "desktop";
  } catch (_) {
    return "unknown";
  }
};

/**
 * Strip a referrer URL down to its bare hostname. Removes scheme,
 * path, query, fragment, and port. Returns null if the input is empty
 * or malformed.
 *
 * Client-side defense-in-depth — backend also re-strips this. We never
 * want a full URL with potentially user-identifying query params (e.g.
 * `?email=...`) to even leave the browser.
 */
const _referrerDomainOnly = () => {
  if (typeof document === "undefined") return null;
  const raw = document.referrer || "";
  if (!raw) return null;
  try {
    return new URL(raw).hostname || null;
  } catch (_) {
    return null;
  }
};

/**
 * Fire a first-party analytics event. Stealth-aware (hard no-op),
 * privacy-preserving (strips IP/UA/referrer), and never throws.
 *
 * Allowed event names are validated server-side against a whitelist —
 * unknown event names are silently dropped (200 OK).
 */
export const trackFP = (event, props = undefined) => {
  if (usesPostHog()) {
    capturePostHog(event, { ...props, locale: i18n?.language, device_type: _detectDeviceType() });
    return;
  }
  if (!event || typeof event !== "string") return;
  // Stealth means no behavioral analytics at all. Check before creating
  // an analytics session id so even the identifier remains absent.
  if (_isStealth()) return;
  // The production-parity /demo experience stores nothing, including
  // anonymous analytics or a browser session identifier.
  if (_isEphemeralDemoRoute()) return;

  // Public Demo Mode (demo.trubeaute.com) — auto-tag every event
  // with `demo_mode: true` so the ingest layer + admin dashboards can
  // filter demo traffic out of production metrics. Session id also
  // switches to the demo id so a demo user isn't cross-linked with
  // any real session on the same browser.
  const inDemoMode =
    typeof window !== "undefined" && !!window.__trubeaute_demo_mode;
  const demoSessionId =
    inDemoMode && typeof window !== "undefined"
      ? window.__trubeaute_demo_session_id || null
      : null;

  const payload = {
    event,
    route:
      typeof window !== "undefined" ? window.location.pathname : undefined,
    locale: (i18n && i18n.language) ? String(i18n.language).slice(0, 5) : undefined,
    device_type: _detectDeviceType(),
    referrer_domain: _referrerDomainOnly(),
    session_id: demoSessionId || _getSessionId(),
    stealth_mode: false,
    demo_mode: inDemoMode || undefined,
    props: props || undefined,
  };

  try {
    // No withCredentials — keep this endpoint cookie-free so even if
    // a session cookie exists, it cannot be associated with the event.
    // This is the privacy invariant the backend depends on.
    axios.post(`${API}/analytics/event`, payload, { timeout: 6000 })
      .catch((err) => {
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.debug("[trackFP] post failed:", err?.message || err);
        }
      });
  } catch (err) {
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.debug("[trackFP] threw:", err?.message || err);
    }
  }
};

// Re-export the stealth detector so other modules (e.g. firstPartyAnalytics
// usage inside ConditionalProviders) can read it without re-importing
// safeLocal + storageKeys.
export const isStealth = _isStealth;
